/**
 * Loads a concept's full body in two requests instead of one per creative:
 * first its CREATIVEs, then every ASSET of those creatives in a single batched
 * query. Nothing here is status-filtered, since a visible concept should show all
 * of its creatives and assets (failed, pending, and removed included), each
 * carrying its own badge. Batching also lets the concept show one loader for the
 * whole body rather than a spinner per creative.
 *
 * Both queries name their parents by row `_id` (parentEntityIds), never by
 * sourceEntityId: ADSP source ids embed free-text titles (`#`, `&`, `(`, ...) that
 * the SigV4 request signer mis-canonicalizes, which gets the request rejected at
 * API Gateway. The response still joins assets to creatives by parentSourceEntityId.
 */
import { useMemo } from 'react';
import type {
  PaginatedMetaPaginationDocumented,
  PublisherEntityDto,
} from 'src/store/generated/leela-api-generated';
import { groupByParentSourceEntityId } from './adspEntityHelpers';
import { ADSP_MAX_PAGE_SIZE, useAdspEntityLevel } from './useAdspEntityLevel';

interface ConceptCreativeTreeArgs {
  campaignId: string;
  /** The concept row's `_id`; the tree stays unloaded without it. */
  conceptId?: string;
  /** Defers both requests until the concept is first expanded. */
  skip: boolean;
}

/** Paging controls for one of the two batched levels. */
export interface TreeLevelPaging {
  isFetching: boolean;
  page: number;
  pagination?: PaginatedMetaPaginationDocumented;
  setPage: (page: number) => void;
}

export interface ConceptCreativeTree {
  assetPaging: TreeLevelPaging;
  assetsByCreative: Map<string, PublisherEntityDto[]>;
  creativePaging: TreeLevelPaging;
  creatives: PublisherEntityDto[];
  isError: boolean;
  isLoading: boolean;
}

const NO_ENTITIES: PublisherEntityDto[] = [];
const NO_IDS: string[] = [];

export function useConceptCreativeTree ({
  campaignId,
  conceptId,
  skip,
}: ConceptCreativeTreeArgs): ConceptCreativeTree {
  const conceptIds = useMemo(() => (isPresentId(conceptId) ? [conceptId] : NO_IDS), [conceptId]);
  const creativeLevel = useAdspEntityLevel({
    campaignId,
    pageSize: ADSP_MAX_PAGE_SIZE,
    parentEntityIds: conceptIds,
    publisherEntityType: 'CREATIVE',
    skip: skip || conceptIds.length === 0,
  });
  const creatives = (creativeLevel.query.data?.data ?? NO_ENTITIES) as PublisherEntityDto[];
  const creativeIds = useMemo(() => readRowIds(creatives), [creatives]);

  const assetLevel = useAdspEntityLevel({
    campaignId,
    pageSize: ADSP_MAX_PAGE_SIZE,
    parentEntityIds: creativeIds,
    publisherEntityType: 'ASSET',
    skip: skip || creativeIds.length === 0,
  });
  // currentData, not data: after a creative page change, the previous page's assets must not be
  // attributed to the new creatives while the new batch is in flight.
  const assets = (assetLevel.query.currentData?.data ?? NO_ENTITIES) as PublisherEntityDto[];
  const assetsByCreative = useMemo(() => groupByParentSourceEntityId(assets), [assets]);

  const isAwaitingAssets = creativeIds.length > 0 && !assetLevel.query.isError &&
    assetLevel.query.currentData === undefined;

  return {
    assetPaging: toPaging(assetLevel),
    assetsByCreative,
    creativePaging: toPaging(creativeLevel),
    creatives,
    isError: creativeLevel.query.isError || assetLevel.query.isError,
    isLoading: creativeLevel.query.isLoading || isAwaitingAssets,
  };
}

function isPresentId (id: string | null | undefined): id is string {
  return id != null && id.length > 0;
}

function readRowIds (entities: readonly PublisherEntityDto[]): string[] {
  return entities.flatMap(entity => (isPresentId(entity._id) ? [entity._id] : []));
}

function toPaging ({ page, query, setPage }: ReturnType<typeof useAdspEntityLevel>): TreeLevelPaging {
  return {
    isFetching: query.isFetching,
    page,
    pagination: query.data?.meta.pagination,
    setPage,
  };
}
